import React, { useState } from 'react';
import {
  ScorePairingFlowProps,
  ScorePairingFlowSteps,
} from './scoreSettings.d';
import StartPairing from './iScoreSteps/StartPairing';
import Stepper from './iScoreSteps/Stepper';
import SHDText from '@ui/Text/SHDText';
import SHDCallout from '@ui/Callout/SHDCallout';
import { Box } from 'grommet';
import CopyToClipboard from 'react-copy-to-clipboard';
import SHDImage from '@ui/Image/SHDImage';
import SHDInput from '@ui/Input/SHDInput';
import SHDBackButton from '@ui/BackButton/SHDBackButton';
import Toast from '@components/Toast';
import useIScore, { TeamIScoreResponse } from '@hooks/useIScore';
import SHDSelectable from '@ui/Selectable/SHDSelectable';

const ScorePairingFlow: React.FC<ScorePairingFlowProps> = ({
  teamId,
  teamPin,
  teamHandle,
  onClose,
  step = ScorePairingFlowSteps.START_PAIRING,
}) => {
  const [currentStep, setCurrentStep] = useState<ScorePairingFlowSteps>(step);
  const [copied, setCopied] = useState(false);
  const [customerId, setCustomerId] = useState<string>('');
  const [scoreTeamId, setScoreTeamId] = useState<string>('');
  const [iScoreTeamData, setIscoreTeamData] = useState<TeamIScoreResponse[]>();
  const [customerIdError, setCustomerIdError] = useState<string>('');
  const [scoreTeamIdError, setScoreTeamIdError] = useState<string>('');
  const [teamNotFind, setTeamNotFind] = useState<string>('');
  const { fetchIscoreTeams, pairWithIscoreTeam, isSyncing, iScoreAfterRoster } =
    useIScore(teamId);
  const noTeamFoundStringStep6 =
    'We could not find your team. Please confirm you imported your team, iScorecast is ON, and your team has at least one game.';
  const noTeamFoundStringStep2 =
    'Team not found. Make sure your customer ID is correct, iScorecast is on, and your team has at least one game.';
  const cantImportTeamStringStep3 =
    'We could not import your team, please try again.';

  const handleStep2UseExistingTeam = async () => {
    setCustomerIdError('');
    if (!customerId) {
      setCustomerIdError('Please enter a valid Customer ID');
    } else {
      try {
        const teamData = await fetchIscoreTeams(customerId);
        if (teamData?.teams?.length === 0) {
          setCustomerIdError(noTeamFoundStringStep2);
        } else {
          setIscoreTeamData(teamData.teams);
          setCurrentStep(ScorePairingFlowSteps.USE_EXISTING_TEAM_STEP_3);
        }
      } catch (error) {
        setCustomerIdError(noTeamFoundStringStep2);
      }
    }
  };

  const handleStep3UseExistingTeam = async () => {
    setScoreTeamIdError('');
    if (!scoreTeamId) {
      setScoreTeamIdError('Please select a team to import');
    } else {
      try {
        const teamData = await pairWithIscoreTeam(customerId, {
          teamId,
          scoreTeamId,
        });
        setScoreTeamIdError('');
        if (!teamData) {
          setScoreTeamIdError(cantImportTeamStringStep3);
        } else {
          onClose();
        }
      } catch (error) {
        setScoreTeamIdError(cantImportTeamStringStep3);
      }
    }
  };

  const handleStep6 = async () => {
    setTeamNotFind('');
    try {
      const syncResponse = await iScoreAfterRoster();
      if (syncResponse) {
        onClose();
      } else {
        setTeamNotFind(noTeamFoundStringStep6);
      }
    } catch (error) {
      setTeamNotFind(noTeamFoundStringStep6);
    }
  };

  const handleSteps = () => {
    switch (currentStep) {
      case ScorePairingFlowSteps.START_PAIRING:
        return (
          <StartPairing
            onTransfer={() => setCurrentStep(ScorePairingFlowSteps.STEP_1)}
            useExistingTeam={() =>
              setCurrentStep(ScorePairingFlowSteps.USE_EXISTING_TEAM_STEP_1)
            }
          />
        );
      case ScorePairingFlowSteps.STEP_1:
        return (
          <Stepper
            onNext={() => setCurrentStep(ScorePairingFlowSteps.STEP_2)}
            overTitle="TRANSFER ROSTER TO ISCORE"
            title="Step 1"
          >
            <Box gap="xlarge" margin={{ bottom: 'xxlarge' }}>
              <SHDText style="subhead" color="black-alpha-80">
                Download the iScore mobile app from the{' '}
                <a
                  href="https://apps.apple.com/no/app/iscore-baseball-and-softball/id364364675"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#007BFF', // Use your design system's link color if needed
                    textDecoration: 'underline',
                  }}
                >
                  Apple App Store
                </a>{' '}
                or from the{' '}
                <a
                  href="https://play.google.com/store/apps/details?id=com.fasterthanmonkeys.iscore&hl=en_US"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#007BFF',
                    textDecoration: 'underline',
                  }}
                >
                  Google Play Store
                </a>
                .
              </SHDText>

              <SHDText style="subhead" color="black-alpha-80">
                Open the app. Go to “OPTIONS”. Turn on the “iScorecast” switch.
              </SHDText>
            </Box>
            <Box>
              <img src="../iscore/iscore_transfer_step_1.png" />
            </Box>
          </Stepper>
        );
      case ScorePairingFlowSteps.STEP_2:
        return (
          <Stepper
            onNext={() => setCurrentStep(ScorePairingFlowSteps.STEP_3)}
            overTitle="TRANSFER ROSTER TO ISCORE"
            title="Step 2"
          >
            <Box gap="xlarge" margin={{ bottom: 'xxlarge' }}>
              <SHDText style="subhead" color="black-alpha-80">
                Open the app. Go to “TEAM MANAGER”. Then press “DISCOVER TEAM”.
              </SHDText>
            </Box>
            <Box>
              <img src="../iscore/iscore_transfer_step_2.png" />
            </Box>
          </Stepper>
        );
      case ScorePairingFlowSteps.STEP_3:
        return (
          <Stepper
            onNext={() => {
              setCurrentStep(ScorePairingFlowSteps.STEP_4), setTeamNotFind('');
            }}
            overTitle="TRANSFER ROSTER TO ISCORE"
            title="Step 3"
          >
            <Box gap="xlarge" margin={{ bottom: 'xxlarge' }}>
              <SHDText style="subhead" color="black-alpha-80">
                Select “sidelineHD” from the options listed.
              </SHDText>
            </Box>
            <Box>
              <img src="../iscore/iscore_transfer_step_3.png" />
            </Box>
          </Stepper>
        );
      case ScorePairingFlowSteps.STEP_4:
        return (
          <Stepper
            onNext={() => setCurrentStep(ScorePairingFlowSteps.STEP_5)}
            overTitle="TRANSFER ROSTER TO ISCORE"
            title="Step 4"
            loading={isSyncing}
          >
            <Box gap="xlarge" margin={{ bottom: 'xxlarge' }}>
              <SHDText style="subhead" color="black-alpha-80">
                Enter the below handle and PIN into the iScore app and then
                press “Login”. When you see your sidelineHD roster, press
                “IMPORT”.
              </SHDText>

              <SHDCallout direction="column" round="medium" pad="large">
                <Box width={'100%'} gap="large">
                  <Box direction="row" justify="between">
                    <SHDText style="subhead">Team handle</SHDText>
                    <CopyToClipboard
                      text={teamHandle}
                      onCopy={() => setCopied(true)}
                    >
                      <Box direction="row" gap={'4px'}>
                        <SHDText style="subhead" color="gray-700">
                          {teamHandle}
                        </SHDText>
                        <SHDImage
                          alt="copy-icon"
                          src="../icons/copy.svg"
                          width={20}
                          height={20}
                        />
                      </Box>
                    </CopyToClipboard>
                  </Box>
                  <Box direction="row" justify="between">
                    <SHDText style="subhead">Team PIN</SHDText>
                    <CopyToClipboard
                      text={teamPin}
                      onCopy={() => setCopied(true)}
                    >
                      <Box direction="row" gap={'4px'}>
                        <SHDText style="subhead" color="gray-700">
                          {teamPin}
                        </SHDText>

                        <SHDImage
                          alt="copy-icon"
                          src="../icons/copy.svg"
                          width={20}
                          height={20}
                        />
                      </Box>
                    </CopyToClipboard>
                  </Box>
                </Box>
              </SHDCallout>
              {copied && (
                <Toast
                  label="Copied to clipboard!"
                  background="status-ok"
                  duration={3000}
                  onClose={() => {
                    setCopied(false);
                  }}
                />
              )}
            </Box>
            <Box>
              <img src="../iscore/iscore_transfer_step_4.png" />
            </Box>
          </Stepper>
        );
      case ScorePairingFlowSteps.STEP_5:
        return (
          <Stepper
            onNext={() => setCurrentStep(ScorePairingFlowSteps.STEP_6)}
            overTitle="TRANSFER ROSTER TO ISCORE"
            title="Step 5"
          >
            <Box gap="xlarge" margin={{ bottom: 'xxlarge' }}>
              <SHDText style="subhead" color="black-alpha-80">
                Exit back out to the iScore home page, and select “GAME
                MANAGER”. Press “ADD” at the top right of the screen.
              </SHDText>
            </Box>
            <Box>
              <img src="../iscore/iscore_transfer_step_5.png" />
            </Box>
          </Stepper>
        );
      case ScorePairingFlowSteps.STEP_6:
        return (
          <Stepper
            onNext={handleStep6}
            title="Step 6"
            overTitle="TRANSFER ROSTER TO ISCORE"
            step={ScorePairingFlowSteps.STEP_6}
            loading={isSyncing}
          >
            <Box gap="xlarge" margin={{ bottom: 'xxlarge' }}>
              {teamNotFind && (
                <SHDText style="subhead" color="red">
                  {teamNotFind}
                </SHDText>
              )}
              <SHDText style="subhead" color="black-alpha-80">
                Create a fake game with your newly created team as the Home and
                Visitor. Leave the rest of the options default or blank. Then
                press “DONE” at the top right of the screen.
              </SHDText>
            </Box>
            <Box>
              <img src="../iscore/iscore_transfer_step_6.png" />
            </Box>
          </Stepper>
        );
      case ScorePairingFlowSteps.USE_EXISTING_TEAM_STEP_1:
        return (
          <Stepper
            onNext={() =>
              setCurrentStep(ScorePairingFlowSteps.USE_EXISTING_TEAM_STEP_2)
            }
            title="Step 1"
            overTitle="USE EXISTING ISCORE TEAM"
          >
            <Box gap="xlarge" margin={{ bottom: 'xxlarge' }}>
              <SHDText style="subhead" color="black-alpha-80">
                Download the iScore mobile app from the{' '}
                <a
                  href="https://apps.apple.com/no/app/iscore-baseball-and-softball/id364364675"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#007BFF', // Use your design system's link color if needed
                    textDecoration: 'underline',
                  }}
                >
                  Apple App Store
                </a>{' '}
                or from the{' '}
                <a
                  href="https://play.google.com/store/apps/details?id=com.fasterthanmonkeys.iscore&hl=en_US"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#007BFF',
                    textDecoration: 'underline',
                  }}
                >
                  Google Play Store
                </a>
                .
              </SHDText>
              <SHDText style="subhead" color="black-alpha-80">
                Open the app. Go to “OPTIONS”. Turn on the “iScorecast” switch.
              </SHDText>
            </Box>
            <Box>
              <img src="../iscore/iscore_transfer_step_1.png" />
            </Box>
          </Stepper>
        );
      case ScorePairingFlowSteps.USE_EXISTING_TEAM_STEP_2:
        return (
          <Stepper
            onNext={handleStep2UseExistingTeam}
            title="Step 2"
            overTitle="USE EXISTING ISCORE TEAM"
            loading={isSyncing}
          >
            <Box gap="xlarge" margin={{ bottom: 'xxlarge' }}>
              <SHDText style="subhead" color="black-alpha-80">
                Within “Options”, find your “CUSTOMER ID” at the bottom of the
                screen. Enter this value into the field below.
              </SHDText>
              <SHDInput
                label=""
                placeholder="Customer ID"
                error={customerIdError ? { message: customerIdError } : null}
                onChange={(value) => setCustomerId(value)}
              />
            </Box>

            <Box>
              <img src="../iscore/iscore_existing_roster_step_2.png" />
            </Box>
          </Stepper>
        );
      case ScorePairingFlowSteps.USE_EXISTING_TEAM_STEP_3:
        return (
          <Stepper
            onNext={handleStep3UseExistingTeam}
            title="Step 3: Select your team"
            overTitle="USE EXISTING ISCORE TEAM"
            step={ScorePairingFlowSteps.USE_EXISTING_TEAM_STEP_3}
            loading={isSyncing}
          >
            <Box margin={{ top: 'xxsmall' }} gap="xsmall">
              {iScoreTeamData &&
                iScoreTeamData?.map((team, index) => (
                  <SHDSelectable
                    key={team.scoreTeamId + index}
                    round="16px"
                    id={team.scoreTeamId + index}
                    onSelected={() => setScoreTeamId(team.scoreTeamId)}
                    isActive={team.scoreTeamId === scoreTeamId}
                    pad={{ vertical: 'medium', left: 'small' }}
                  >
                    <Box alignSelf="start">
                      <SHDText style="subhead">{team.name}</SHDText>
                    </Box>
                  </SHDSelectable>
                ))}
              {scoreTeamIdError && (
                <SHDText style="footnote" color="red">
                  {scoreTeamIdError}
                </SHDText>
              )}
            </Box>
          </Stepper>
        );
    }
  };

  const handleGoBack = () => {
    switch (currentStep) {
      case ScorePairingFlowSteps.STEP_1:
        setCurrentStep(ScorePairingFlowSteps.START_PAIRING);
        break;
      case ScorePairingFlowSteps.STEP_2:
        setCurrentStep(ScorePairingFlowSteps.STEP_1);
        break;
      case ScorePairingFlowSteps.STEP_3:
        setCurrentStep(ScorePairingFlowSteps.STEP_2);
        break;
      case ScorePairingFlowSteps.STEP_4:
        setCurrentStep(ScorePairingFlowSteps.STEP_3);
        break;
      case ScorePairingFlowSteps.STEP_5:
        setCurrentStep(ScorePairingFlowSteps.STEP_4);
        break;
      case ScorePairingFlowSteps.STEP_6:
        setCurrentStep(ScorePairingFlowSteps.STEP_5);
        break;

      // Handle "Use Existing Team" flow
      case ScorePairingFlowSteps.USE_EXISTING_TEAM_STEP_1:
        setCurrentStep(ScorePairingFlowSteps.START_PAIRING);
        break;
      case ScorePairingFlowSteps.USE_EXISTING_TEAM_STEP_2:
        setCurrentStep(ScorePairingFlowSteps.USE_EXISTING_TEAM_STEP_1);
        break;
      case ScorePairingFlowSteps.USE_EXISTING_TEAM_STEP_3:
        setCustomerId('');
        setCurrentStep(ScorePairingFlowSteps.USE_EXISTING_TEAM_STEP_2);
        break;

      default:
        break;
    }
  };

  return (
    <>
      {currentStep !== ScorePairingFlowSteps.START_PAIRING && (
        <Box
          style={{
            position: 'absolute',
            top: '-25px',
          }}
          alignSelf="start"
          pad={{ left: 'large' }}
        >
          <SHDBackButton onClick={handleGoBack} />
        </Box>
      )}
      {handleSteps()}
    </>
  );
};

export default ScorePairingFlow;
